import { useCallback, useLayoutEffect, useRef, useState } from 'react'

const TAG_HEIGHT = 22
const TAG_GAP = 4
const MAX_ROWS = 2
/** 2 rows of 22px tags with 4px gap = 48px */
const MAX_HEIGHT = MAX_ROWS * TAG_HEIGHT + (MAX_ROWS - 1) * TAG_GAP

export const useVisibleTagCount = (totalCount: number) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState<number | null>(null)
  const isMeasuring = visibleCount === null

  const measure = useCallback(() => {
    const container = containerRef.current

    if (!container || totalCount === 0) {
      return
    }

    const tagEls = container.querySelectorAll(
      '.TagBadge:not(.TagBadge--overflow)',
    )
    const containerTop = container.offsetTop
    let count = 0

    for (const el of tagEls) {
      const htmlEl = el as HTMLElement

      if (htmlEl.offsetTop + htmlEl.offsetHeight - containerTop > MAX_HEIGHT) {
        break
      }

      count += 1
    }

    if (count < totalCount) {
      count = Math.max(1, count - 1)
    }

    setVisibleCount(count)
  }, [totalCount])

  useLayoutEffect(() => {
    const container = containerRef.current

    if (!container) {
      return
    }

    const resizeTarget = container.parentElement ?? container
    let lastWidth: number | null = null
    let rafId: number | null = null

    const scheduleMeasurement = () => {
      setVisibleCount(null)

      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }

      // Wait for the table to finish sizing its columns before reading wrapped offsets.
      rafId = requestAnimationFrame(() => {
        rafId = requestAnimationFrame(() => {
          rafId = null
          measure()
        })
      })
    }

    const resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) {
        return
      }

      const newWidth = entry.contentRect.width

      if (lastWidth === null) {
        lastWidth = newWidth

        return
      }

      if (newWidth === lastWidth) {
        return
      }

      lastWidth = newWidth
      scheduleMeasurement()
    })

    scheduleMeasurement()
    resizeObserver.observe(resizeTarget)

    return () => {
      resizeObserver.disconnect()

      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [measure])

  return {
    containerRef,
    isMeasuring,
    visibleCount: visibleCount ?? totalCount,
  }
}
